import React, { useEffect, useMemo, useState } from "react";

import { Box, Stack, Typography } from "@mui/material";
import { Graph, KPIs } from "components/Deal-dashboard";
import { useActionHook } from "hooks";
import { useClientActions, useClientIdSelector } from "store";
import type { DateRangeValue, ReportFilters } from "store/types";

import { Entities } from "./Entities";
import { LinkedDeals } from "./LinkedDeals";
import { Tasks } from "./Tasks";

export const OverviewTab: React.FC = () => {
  const clientId = useClientIdSelector();

  const isInvalidClientId = !clientId || isNaN(clientId);

  const { getClientOverviewDealsAction, getAllocations } = useClientActions();

  // Scope filter for the reusable deal-dashboard KPIs + Graph (client instead of a deal).
  const clientFilters = useMemo<ReportFilters>(() => ({ client_id: clientId ? [clientId] : [] }), [clientId]);

  const loadingOpts = useMemo(() => ({ defaultLoadingState: true }), []);
  const {
    data: dealsData,
    loading: dealsLoading,
    error: dealsError,
    action: getClientOverviewDeals
  } = useActionHook(getClientOverviewDealsAction, loadingOpts);
  const {
    data: allocations,
    loading: entitiesLoading,
    error: entitiesError,
    action: getEntities
  } = useActionHook(getAllocations, loadingOpts);

  // Date range for the KPI strip (owned here, like the deal dashboard passes to <KPIs/>).
  const [kpiDateRange, setKpiDateRange] = useState<DateRangeValue<string>>({ value: "fromInception" });

  // Linked deals + entities preview: fetched once the client id is known / changes.
  useEffect(() => {
    if (isInvalidClientId) return;
    getClientOverviewDeals(clientId);
    getEntities(clientId);
  }, [clientId, isInvalidClientId]);

  // ---- Data unwrapping. Deals come from /overview/deals; entities from the
  // allocations endpoint; the Tasks widget fetches its own tasks. ----
  const entitiesList = allocations ?? [];
  const entitiesCount = allocations?.length ?? 0;

  const dealsList = dealsData?.deals ?? [];
  const dealsCount = dealsData?.total ?? dealsList.length;

  // Each widget shows its own loading state from its own request (no page-wide skeleton).
  const isError = dealsError || entitiesError;

  // 1. Invalid State
  if (isInvalidClientId) {
    return (
      <Box sx={{ p: 4, textAlign: "center", minHeight: "200px" }}>
        <Typography variant="h6">Invalid client ID</Typography>
      </Box>
    );
  }

  // 2. Error State
  if (isError) {
    return (
      <Box sx={{ p: 4, textAlign: "center", minHeight: "200px" }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Couldn't load client data.
        </Typography>
      </Box>
    );
  }

  // 3. Populated State — each widget renders its own loading state.
  return (
    <Box
      data-testid="overview-tab"
      sx={{
        height: "100%",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 3,
        pr: 1,
        // Prevent flex children from collapsing when content exceeds viewport height
        "& > *": { flexShrink: 0 }
      }}
    >
      {/* Widget 1: KPI strip — reusable deal-dashboard <KPIs/>, scoped to the client */}
      <KPIs
        dateRange={kpiDateRange}
        setDateRange={setKpiDateRange}
        title="Client Overview"
        pageType="cubicClientDashboard"
        filters={clientFilters}
      />

      {/* Widget 2.5: NAV Graph (reusable deal-dashboard Graph, scoped to the client) + Tasks */}
      <Stack direction="row" gap={3} sx={{ width: "100%" }}>
        <Graph filters={clientFilters} />
        <Stack sx={{ maxWidth: 402, minWidth: 402, flexShrink: 0 }}>
          <Tasks clientId={clientId} />
        </Stack>
      </Stack>

      {/* Widget 2: Entities */}
      <Entities entities={entitiesList} count={entitiesCount} clientId={clientId} loading={entitiesLoading} />

      {/* Widget 3: Linked Deals */}
      <LinkedDeals deals={dealsList} count={dealsCount} postfix={dealsData?.postfix} loading={dealsLoading} />
    </Box>
  );
};

export default OverviewTab;
